import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api.ts";
import { User } from "@/javascript/user-objects.ts";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck, IconUser } from "@tabler/icons-react";
import { useCallback, useContext, useEffect } from "react";

interface StudentUsernameEditorModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	user: User | null;
	onChange?: () => void;
}

const StudentUsernameEditorModal: React.FC<StudentUsernameEditorModalProps> = ({ user, showModal, setShowModal, onChange }) => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("This modal must be a child of SessionContext.");
	}

	const form = useForm({
		initialValues: {
			newUsername: "",
		},
		validate: {
			newUsername: (value) => {
				if (value.length < 3) return "Username must be at least 3 characters long.";
				if (value.length > 30) return "Username must not exceed 30 characters.";
				return null;
			},
		},
	});

	useEffect(() => {
		if (user) {
			form.setValues({ newUsername: user.username });
		}
	}, [user]);

	const handleSubmit = useCallback(async ({ newUsername }: typeof form.values) => {
		if (session.token === null || user === null) {
			return;
		}

		const notificationId = Utils.displayNotification(
			"Updating username....",
			null,
			"blue",
			"loading"
		);

		try {
			await ConductionAPI.updateUsername(user, newUsername, session.token);
			Utils.updateNotification(
				notificationId,
				`Username successfully updated from ${user.username} to ${newUsername}.`,
				undefined,
				"green",
				<IconCheck size={16} />
			);
			form.reset();
			onChange?.();
			setShowModal(false);
		} catch (error) {
			console.error("Error updating user information", error);
			Utils.updateNotification(
				notificationId,
				"Error: The entered username is already in use.",
				undefined,
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [user, session.token]);

	if (user === null) return null;

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="User Settings"
			centered
			size="md"
			styles={{ title: { fontSize: 26, fontVariant: "all-small-caps" } }}
			withCloseButton
		>
			<Flex
				align="stretch"
				justify="space-between"
				direction={"column"}
			>
				<Text size="lg">
					Change username for user: {user.username}
				</Text>
				<Box>
					<Stack>
						<form onSubmit={form.onSubmit(handleSubmit)}>
							<Stack>
								<TextInput
									label={
										<Group>
											<IconUser size={20} />
											<Text>New Username</Text>
										</Group>
									}
									{...form.getInputProps("newUsername")}
									required
									withAsterisk={false}
								/>

								<Button
									type="submit"
									radius="xl"
									size='sm'
									loading={form.submitting}
									disabled={form.values.newUsername === user.username}
								>
									Update Username
								</Button>
							</Stack>
						</form>
					</Stack>
				</Box>
			</Flex>
		</Modal>
	);
};

export default StudentUsernameEditorModal;
